import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
// import { InputField, TextAreaField } from '../../../components/formFields';
// import { InputField, TextAreaField } from '../components/commons/formFields';
// import { useResponseHandler } from '../../../commons/hooks/useResponseHandler';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';
import { validateResponse } from '../../../../commons/utils';
import { storeCreateAreasRating, storeUpdateAreasRating } from '../../../../store/ratings/thunks';
import { areaFormModel } from '../formik/formModels/areaFormModel';
import { areaInitialValues } from '../formik/formModels/areaInitialValues';
import { areaValidationSchema } from '../formik/formModels/areaValidationSchema';
import { ModalSessionExpired } from '../../../../components/generics/ModalSessionExpired';
import InputField from '../../../../components/formFields/InputField';
import TextAreaField from '../../../../components/formFields/TextAreaField';
// import { ModalSessionExpired } from '../components/modals/auth/ModalSessionExpired';

interface FormAreaModalProps {
  open: boolean;
  handleClose: () => void;
  areaData?: any | null;
  refreshData: () => void;
  isViewMode?: boolean;
}

export const FormAreaModal: React.FC<FormAreaModalProps> = ({
  open,
  handleClose,
  areaData,
  refreshData,
  isViewMode = false,
}) => {
  const dispatch = useDispatch();
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const isEditMode = Boolean(areaData && areaData.id);

  const {
    formField: { name, description },
  } = areaFormModel;

  const currentInitialValues = isEditMode
    ? {
        [name.name]: areaData.name || '',
        [description.name]: areaData.description || '',
      }
    : areaInitialValues;

  const onSubmit = async (values: any, { setSubmitting }: any) => {
    let response;
    if (isEditMode) {
      response = await dispatch(storeUpdateAreasRating(areaData.id, values));
    } else {
      response = await dispatch(storeCreateAreasRating(values));
    }

    await handleResponse(response, isEditMode ? 'editar área' : 'crear área', true);

    setSubmitting(false);

    if (response.ok) {
      handleClose();
    }
  };

  return (
    <>
      {ResponseComponents}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 3, display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'rgba(105, 108, 255, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              color: '#696cff',
            }}
          >
            {isViewMode ? (
              <VisibilityOutlinedIcon sx={{ fontSize: 24 }} />
            ) : isEditMode ? (
              <EditOutlinedIcon sx={{ fontSize: 24 }} />
            ) : (
              <SaveOutlinedIcon sx={{ fontSize: 24 }} />
            )}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#32475CDE', lineHeight: 1.2 }}>
              {isViewMode ? 'Detalles del Área' : isEditMode ? 'Editar Área' : 'Crear Área'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#32475C99', mt: 0.5 }}>
              {isViewMode
                ? 'Información del área de calificación'
                : isEditMode
                  ? 'Actualiza la información del área'
                  : 'Agrega una nueva área de calificación'}
            </Typography>
          </Box>

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          initialValues={currentInitialValues}
          validationSchema={areaValidationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ isSubmitting, isValid, dirty }) => (
            <Form id={areaFormModel.formId}>
              <DialogContent
                dividers
                sx={{ p: 3, borderTop: '1px solid #32475C1F', borderBottom: '1px solid #32475C1F' }}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <InputField
                      name={name.name}
                      label={name.label}
                      fullWidth
                      autoComplete="off"
                      placeholder="Ej. Restaurante, Recepción"
                      InputProps={{ readOnly: isViewMode }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextAreaField
                      name={description.name}
                      label={description.label}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Detalles sobre el área..."
                      InputProps={{ readOnly: isViewMode }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        backgroundColor: 'rgba(105, 108, 255, 0.08)',
                        p: 2,
                        borderRadius: 2,
                        mt: 1,
                      }}
                    >
                      <InfoOutlinedIcon sx={{ color: '#696cff', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#696cff' }}>
                        {isViewMode
                          ? 'Estás en modo de solo lectura. No puedes modificar esta información.'
                          : isEditMode
                            ? 'Puedes modificar el nombre y la descripción. Estos cambios se reflejarán en las encuestas vinculadas.'
                            : 'Esta área podrá ser seleccionada al configurar encuestas y reglas.'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 3, justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                >
                  {isViewMode ? 'Cerrar' : 'Cancelar'}
                </Button>
                {!isViewMode && (
                  <Button
                    type="submit"
                    disabled={isSubmitting || (!isValid && !dirty)}
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0px 4px 8px -4px rgba(105, 108, 255, 0.42)',
                    }}
                  >
                    {isEditMode ? 'Guardar Cambios' : 'Crear Área'}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};
